import { ComponentErrorBoundary, MarketCard } from '@/components';
import type { EventMarket, EventResponse } from '@/types';
import { isMarketResolved } from '@/utils';

type MarketCardListProps = {
  events: EventResponse[];
  onTradeClick: (marketId: string, outcomeId: string, isBuyOrder: boolean) => void;
  isResolvedFilter?: boolean;
  activeCategorySlug?: string;
  selectedFilter?: string;
};

type EventMarketCardItemProps = {
  event: EventResponse;
  representativeMarket: EventMarket;
  onTradeClick: (marketId: string, outcomeId: string, isBuyOrder: boolean) => void;
  activeCategorySlug?: string;
  selectedFilter?: string;
};

const EventMarketCardItem = ({
  event,
  representativeMarket,
  onTradeClick,
  activeCategorySlug,
  selectedFilter,
}: EventMarketCardItemProps) => {
  return (
    <ComponentErrorBoundary componentName={`EventMarketCard-${representativeMarket.id}`}>
      <MarketCard
        event={event}
        market={representativeMarket}
        onTradeClick={onTradeClick}
        activeCategorySlug={activeCategorySlug}
        selectedFilter={selectedFilter}
      />
    </ComponentErrorBoundary>
  );
};

export const MarketCardList = ({
  events,
  onTradeClick,
  isResolvedFilter,
  activeCategorySlug,
  selectedFilter,
}: MarketCardListProps) => {
  if (!events || events.length === 0) {
    return null;
  }

  const hasMarkets = (event: EventResponse) => event.markets && event.markets.length > 0;

  const renderMarketCard = (event: EventResponse) => {
    // Pick the first unresolved market to represent the event, if any.
    // Otherwise, pick the first one.
    const activeMarket = event.markets.find((m) => !isMarketResolved(m));
    const representativeMarket = isResolvedFilter
      ? event.markets[0]
      : activeMarket || event.markets[0];

    return (
      <EventMarketCardItem
        key={event.id}
        event={event}
        representativeMarket={representativeMarket}
        onTradeClick={onTradeClick}
        activeCategorySlug={activeCategorySlug}
        selectedFilter={selectedFilter}
      />
    );
  };

  const isValidTrendingEvent = (event: EventResponse) => {
    if (!activeCategorySlug || activeCategorySlug === 'all') {
      if (!!event.gameId && !event.homeTeam && !event.awayTeam) {
        return false;
      }
    }
    return true;
  };

  const marketCards = events.filter(hasMarkets).filter(isValidTrendingEvent).map(renderMarketCard);

  return <>{marketCards}</>;
};

export default MarketCardList;
